import { ReactNode } from "react";
import { EventParticipantRole } from "../../repository/data.default";
import GroupedTargetPicker from "../../controls/GroupedTargetPicker";
import {
  companyTargets,
  departmentUserTargets,
  ParticipantItem,
  TargetGroup,
  platformWideTargets,
} from "./calendarEventForm";

export interface ParticipantTargetSelectProps {
  /** Scopes the offered targets to one company (the company itself, its departments, its members +
   *  assigned affiliates). When absent, platform-wide targets are offered — only an admin reaches
   *  that path, and the backend still enforces targeting. */
  readonly companyUid?: string;
  /** Receives each picked target; the control clears itself after a pick (it adds, it never holds a value). */
  readonly onPick: (item: ParticipantItem) => void;
  /** When set, only the members of these departments are offered (users only — no company/department
   *  targets); an empty list offers nothing and disables the control. Requires `companyUid`. */
  readonly departmentUids?: string[];
  /** The role stamped on picked users (companies/departments always fan out as attendees). */
  readonly role?: EventParticipantRole;
  /** Optional label above the control, styled like a DynamicForm field label. */
  readonly label?: string;
  readonly placeholder?: string;
  /** Optional row pinned above the options inside the dropdown (e.g. an "Add all" toggle). */
  readonly dropdownHeader?: ReactNode;
  /** Toggle mode: the already-picked target values (shown checked; clicking one calls `onUnpick`). */
  readonly selectedValues?: string[];
  /** Target values never offered (e.g. the signed-in creator, who hosts the event anyway). */
  readonly excludeValues?: string[];
  readonly onUnpick?: (item: ParticipantItem) => void;
  readonly disabled?: boolean;
  readonly className?: string;
}

/**
 * The invitation-target picker shared by the calendar event dialogs and the training manage dialog:
 * a thin scope-binding over the generic GroupedTargetPicker — company-scoped (`companyTargets`) or
 * platform-wide (`platformWideTargets`) grouped Companies / Departments / Users targets, with picked
 * users stamped with the given role. Staging vs immediate invite stays the caller's concern.
 */
function ParticipantTargetSelect(props: ParticipantTargetSelectProps) {
  const role = props.role ?? EventParticipantRole.Attendee;
  const excludeFrom = (groups: Promise<TargetGroup[]>) =>
    props.excludeValues?.length
      ? groups.then((loaded) =>
          loaded.map((group) => ({
            ...group,
            value: group.value.filter((item) => !props.excludeValues!.includes(item.value)),
          })),
        )
      : groups;
  const departmentScoped = !!props.departmentUids && !!props.companyUid;
  const noDepartments = departmentScoped && !props.departmentUids!.length;

  return (
    <GroupedTargetPicker<ParticipantItem>
      loadGroups={(text) =>
        excludeFrom(
          departmentScoped
          ? departmentUserTargets(props.companyUid!, props.departmentUids!, text, role)
          : props.companyUid
          ? companyTargets(props.companyUid, text, role)
          : platformWideTargets(text, role),
        )
      }
      // A company switch invalidates the loaded options (they belong to the previous scope).
      // ...as does a change of the department scope.
      resetKey={`${props.companyUid}|${props.departmentUids?.join(",") ?? ""}`}
      onPick={props.onPick}
      label={props.label}
      placeholder={
        noDepartments
          ? "Add a department first"
          : (props.placeholder ?? "Search people, departments or the whole company")
      }
      dropdownHeader={props.dropdownHeader}
      selectedValues={props.selectedValues}
      onUnpick={props.onUnpick}
      disabled={props.disabled || noDepartments}
      className={props.className}
    />
  );
}

export default ParticipantTargetSelect;
